// CaptureFilterChips — single-select status filter row for the field
// capture surface. Used on the buildings list and the rooms list.
//
// Four options: All / Not started / In progress / Completed. Labels
// match the per-item status badges word-for-word so the filter
// vocabulary is consistent with what each row reports about itself.
// `All` resets the filter — no other option implies "all".
// Implementation uses `role="tablist"` + `role="tab"` +
// `aria-selected` so screen readers announce the chips as a switch
// group rather than a generic button row.

import type { ReactElement } from 'react'

export type CaptureStatusFilter =
  | 'all'
  | 'not_started'
  | 'in_progress'
  | 'complete'

interface FilterDescriptor {
  id: CaptureStatusFilter
  label: string
}

const FILTERS: ReadonlyArray<FilterDescriptor> = [
  { id: 'all', label: 'All' },
  { id: 'not_started', label: 'Not started' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'complete', label: 'Completed' },
]

export interface CaptureFilterChipsProps {
  selected: CaptureStatusFilter
  onSelect: (filter: CaptureStatusFilter) => void
  ariaLabel?: string
}

export function CaptureFilterChips({
  selected,
  onSelect,
  ariaLabel = 'Filter by status',
}: CaptureFilterChipsProps): ReactElement {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex gap-1.5 pb-0.5"
      data-testid="capture-filter-chips"
    >
      {FILTERS.map((filter) => {
        const isOn = selected === filter.id
        return (
          <button
            key={filter.id}
            type="button"
            role="tab"
            aria-selected={isOn}
            onClick={() => onSelect(filter.id)}
            className={[
              'inline-flex min-h-9 flex-1 items-center justify-center whitespace-nowrap rounded-full border px-2 py-1.5 text-[11px] font-medium transition-colors sm:flex-initial sm:flex-shrink-0 sm:px-3.5 sm:text-xs',
              isOn
                ? 'border-[color:var(--color-cc-forest)] bg-[color:var(--color-cc-forest)] text-[color:var(--color-cc-cream)]'
                : 'border-[color:var(--color-cc-forest)]/10 bg-white text-[color:var(--color-cc-charcoal)] hover:border-[color:var(--color-cc-forest)]/25',
            ].join(' ')}
          >
            {filter.label}
          </button>
        )
      })}
    </div>
  )
}
